import React, { useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/primitives";
import { Plus, X, WarningCircle, CheckCircle, PaperPlaneTilt, Coin, Receipt } from "@phosphor-icons/react";

const STATUS_STYLES = {
  captured:   "bg-[#f3f4f6] text-[#374151]",
  matched:    "bg-[#dcfce7] text-[#166534]",
  exception:  "bg-[#fee2e2] text-[#991b1b]",
  approved:   "bg-[#dbeafe] text-[#1e40af]",
  paid:       "bg-[#e0e7ff] text-[#3730a3]",
};

function fmt(n) {
  return Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function AccountsPayable() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pos, setPos] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [modal, setModal] = useState(null); // "new" | invoiceDetail
  const [selectedIds, setSelectedIds] = useState([]);
  const [payRunOpen, setPayRunOpen] = useState(false);
  const [form, setForm] = useState({
    supplier_name: "", number: "", invoice_date: new Date().toISOString().slice(0,10),
    due_date: "", amount_gross: "", tax_amount: "", po_ids: [], notes: "",
  });
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [i, p, s] = await Promise.all([
        api.get("/ap/invoices"),
        api.get("/purchase-orders?status=received"),
        api.get("/suppliers"),
      ]);
      setRows(i.data || []);
      setPos(p.data || []);
      setSuppliers(s.data || []);
    } catch (e) { toast.error(formatError(e)); }
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    const s = { captured: 0, matched: 0, exception: 0, approved: 0, paid: 0, unpaid_total: 0 };
    for (const r of rows) {
      s[r.status] = (s[r.status] || 0) + 1;
      if (r.status !== "paid") s.unpaid_total += (r.amount_net || 0) + (r.tax_amount || 0);
    }
    return s;
  }, [rows]);

  async function saveInvoice() {
    if (!form.supplier_name || !form.number || !form.amount_gross) {
      toast.error("Supplier, number and gross amount are required");
      return;
    }
    setBusy(true);
    try {
      const body = {
        ...form,
        amount_gross: Number(form.amount_gross),
        tax_amount: Number(form.tax_amount || 0),
      };
      if (!body.due_date) delete body.due_date;
      await api.post("/ap/invoices", body);
      toast.success("Invoice captured");
      setModal(null);
      setForm({ ...form, number: "", amount_gross: "", tax_amount: "", po_ids: [], notes: "" });
      await load();
    } catch (e) { toast.error(formatError(e)); }
    setBusy(false);
  }

  async function runMatch(id) {
    try {
      const { data } = await api.post(`/ap/invoices/${id}/match`);
      toast[data.match_status === "matched" ? "success" : "warning"](
        `Match: ${data.match_status.toUpperCase()} (${(data.variances || []).length} variance${(data.variances||[]).length===1?"":"s"})`);
      await load();
      const detail = await api.get(`/ap/invoices/${id}`);
      setModal({ kind: "detail", inv: detail.data });
    } catch (e) { toast.error(formatError(e)); }
  }

  async function approve(id, override = false, reason = "") {
    try {
      await api.post(`/ap/invoices/${id}/approve`, { override, override_reason: reason || null });
      toast.success("Invoice approved — journal posted");
      await load();
      setModal(null);
    } catch (e) { toast.error(formatError(e)); }
  }

  async function pay(id) {
    if (!window.confirm("Post the payment journal (Dr AP / Cr Bank)?")) return;
    try {
      await api.post(`/ap/invoices/${id}/pay`, { bank_account_code: "1111" });
      toast.success("Payment posted");
      await load();
      setModal(null);
    } catch (e) { toast.error(formatError(e)); }
  }

  return (
    <div data-testid="ap-page">
      <PageHeader
        overline="Finance / AP"
        title="Supplier Invoices"
        subtitle="Capture, 3-way match, approve and pay — every step lands in the GL automatically."
        testId="ap-header"
      />

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
        {[
          {k: "captured",  label: "Captured",  color: "#71717a"},
          {k: "matched",   label: "Matched",   color: "#166534"},
          {k: "exception", label: "Exceptions",color: "#991b1b"},
          {k: "approved",  label: "Approved",  color: "#1e40af"},
          {k: "paid",      label: "Paid",      color: "#3730a3"},
        ].map((c) => (
          <div key={c.k} className="phizo-card p-3">
            <div className="phizo-label text-[10px]">{c.label}</div>
            <div className="mono text-2xl font-black mt-1" style={{ color: c.color }}>
              {stats[c.k] || 0}
            </div>
          </div>
        ))}
      </div>

      <div className="phizo-card p-4 mb-4 flex items-center gap-3 flex-wrap">
        <div className="text-xs text-[#71717a]">
          Unpaid total: <span className="mono font-bold">K {fmt(stats.unpaid_total)}</span>
        </div>
        {selectedIds.length > 0 && (
          <>
            <span className="text-xs text-[#71717a] border-l border-[#e4e4e7] pl-3">
              {selectedIds.length} selected
            </span>
            <button
              onClick={async () => {
                if (!window.confirm(`Post one payment journal for ${selectedIds.length} invoices?`)) return;
                try {
                  const { data } = await api.post("/ap/payment-run", {
                    invoice_ids: selectedIds, bank_account_code: "1111",
                  });
                  toast.success(`Payment run posted ${data.journal.number} · K ${fmt(data.total)}`);
                  setSelectedIds([]); await load();
                } catch (e) { toast.error(formatError(e)); }
              }}
              data-testid="ap-payrun-btn"
              className="border border-black bg-black text-white px-3 py-1.5 text-xs hover:bg-[#27272a] flex items-center gap-1.5">
              <Coin size={12} weight="bold" /> Run payment
            </button>
          </>
        )}
        <button onClick={() => setModal("new")} data-testid="ap-new-btn"
                className="ml-auto bg-black text-white px-4 py-1.5 text-xs flex items-center gap-1.5 hover:bg-[#27272a]">
          <Plus size={12} weight="bold" /> Capture invoice
        </button>
      </div>

      <div className="phizo-card overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-[#fafafa] border-b border-[#e4e4e7]">
            <tr className="text-left">
              <th className="px-3 py-2 w-8"></th>
              <th className="px-3 py-2 phizo-label text-[9px]">Supplier</th>
              <th className="px-3 py-2 phizo-label text-[9px]">Invoice #</th>
              <th className="px-3 py-2 phizo-label text-[9px]">Date</th>
              <th className="px-3 py-2 phizo-label text-[9px] text-right">Net</th>
              <th className="px-3 py-2 phizo-label text-[9px]">Status</th>
              <th className="px-3 py-2 phizo-label text-[9px]">Match</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="p-6 text-center text-[#71717a]">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="p-6 text-center text-[#71717a]">No invoices yet.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-[#f4f4f5] hover:bg-[#fafafa] cursor-pointer"
                  data-testid={`ap-row-${r.number}`}
                  onClick={async () => {
                    const { data } = await api.get(`/ap/invoices/${r.id}`);
                    setModal({ kind: "detail", inv: data });
                  }}>
                <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                  {r.status === "approved" ? (
                    <input type="checkbox"
                           checked={selectedIds.includes(r.id)}
                           onChange={(e) => setSelectedIds(
                             e.target.checked
                               ? [...selectedIds, r.id]
                               : selectedIds.filter((x) => x !== r.id))}
                           data-testid={`ap-select-${r.number}`}/>
                  ) : null}
                </td>
                <td className="px-3 py-2 font-semibold">{r.supplier_name}</td>
                <td className="px-3 py-2 mono">{r.number}</td>
                <td className="px-3 py-2 mono text-[11px]">{r.invoice_date}</td>
                <td className="px-3 py-2 text-right mono">{fmt(r.amount_net)}</td>
                <td className="px-3 py-2">
                  <span className={`mono text-[10px] px-1.5 py-0.5 ${STATUS_STYLES[r.status] || ""}`}>
                    {r.status.toUpperCase()}
                  </span>
                </td>
                <td className="px-3 py-2">
                  {r.match_status === "matched" && <CheckCircle size={14} weight="fill" className="text-[#059669]" />}
                  {r.match_status === "exception" && <WarningCircle size={14} weight="fill" className="text-[#dc2626]" />}
                  {r.match_status === "review" && <WarningCircle size={14} weight="fill" className="text-[#d97706]" />}
                  {r.match_status === "unmatched" && <span className="text-[10px] text-[#a1a1aa]">—</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  {r.status === "captured" && (
                    <button onClick={(e) => { e.stopPropagation(); runMatch(r.id); }}
                            data-testid={`ap-match-${r.number}`}
                            className="text-[11px] border border-black bg-black text-white px-2 py-1 hover:bg-[#27272a]">
                      Run match
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Capture modal */}
      {modal === "new" && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div className="bg-white w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}
               data-testid="ap-new-modal">
            <div className="px-5 py-3 border-b flex justify-between items-start">
              <div>
                <div className="phizo-label text-[10px] text-[#a1a1aa]">Accounts Payable</div>
                <h3 className="font-heading text-xl font-black tracking-tighter">Capture supplier invoice</h3>
              </div>
              <button onClick={() => setModal(null)} className="p-1 text-[#71717a]"><X size={16} weight="bold" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs col-span-2">
                  <div className="phizo-label text-[10px] mb-1">Supplier</div>
                  <input list="supplier-list" value={form.supplier_name}
                         onChange={(e) => setForm({ ...form, supplier_name: e.target.value })}
                         className="w-full border border-[#e4e4e7] px-3 py-1.5 text-sm outline-none focus:border-black"
                         data-testid="ap-form-supplier"/>
                  <datalist id="supplier-list">
                    {suppliers.map((s) => <option key={s.id} value={s.name} />)}
                  </datalist>
                </label>
                <label className="text-xs">
                  <div className="phizo-label text-[10px] mb-1">Invoice number</div>
                  <input value={form.number}
                         onChange={(e) => setForm({ ...form, number: e.target.value })}
                         className="w-full border border-[#e4e4e7] px-3 py-1.5 text-sm mono outline-none focus:border-black"
                         data-testid="ap-form-number"/>
                </label>
                <label className="text-xs">
                  <div className="phizo-label text-[10px] mb-1">Invoice date</div>
                  <input type="date" value={form.invoice_date}
                         onChange={(e) => setForm({ ...form, invoice_date: e.target.value })}
                         className="w-full border border-[#e4e4e7] px-3 py-1.5 text-sm outline-none focus:border-black"/>
                </label>
                <label className="text-xs">
                  <div className="phizo-label text-[10px] mb-1">Due date (optional)</div>
                  <input type="date" value={form.due_date}
                         onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                         className="w-full border border-[#e4e4e7] px-3 py-1.5 text-sm outline-none focus:border-black"/>
                </label>
                <label className="text-xs">
                  <div className="phizo-label text-[10px] mb-1">Amount gross (ZMW)</div>
                  <input type="number" step="0.01" value={form.amount_gross}
                         onChange={(e) => setForm({ ...form, amount_gross: e.target.value })}
                         className="w-full border border-[#e4e4e7] px-3 py-1.5 text-sm mono outline-none focus:border-black"
                         data-testid="ap-form-gross"/>
                </label>
                <label className="text-xs">
                  <div className="phizo-label text-[10px] mb-1">Of which VAT (optional)</div>
                  <input type="number" step="0.01" value={form.tax_amount}
                         onChange={(e) => setForm({ ...form, tax_amount: e.target.value })}
                         className="w-full border border-[#e4e4e7] px-3 py-1.5 text-sm mono outline-none focus:border-black"/>
                </label>
                <div className="text-xs col-span-2">
                  <div className="phizo-label text-[10px] mb-1">Link to received PO(s)</div>
                  <div className="border border-[#e4e4e7] max-h-40 overflow-y-auto">
                    {pos.length === 0 ? (
                      <div className="p-3 text-[11px] text-[#71717a]">No received POs available.</div>
                    ) : pos.map((p) => (
                      <label key={p.id} className="flex items-center gap-2 px-3 py-1.5 text-xs border-b border-[#f4f4f5] cursor-pointer hover:bg-[#fafafa]">
                        <input type="checkbox" checked={form.po_ids.includes(p.id)}
                               onChange={(e) => setForm({
                                 ...form,
                                 po_ids: e.target.checked
                                   ? [...form.po_ids, p.id]
                                   : form.po_ids.filter((x) => x !== p.id),
                               })}/>
                        <span className="mono font-semibold">{p.number}</span>
                        <span className="flex-1 truncate">{p.supplier}</span>
                        <span className="mono text-[11px] text-[#71717a]">K {fmt(p.total)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <button onClick={() => setModal(null)} className="border border-[#e4e4e7] px-4 py-1.5 text-xs hover:bg-[#f4f4f5]">Cancel</button>
              <button onClick={saveInvoice} disabled={busy} data-testid="ap-save-btn"
                      className="bg-[#002FA7] text-white px-4 py-1.5 text-xs font-medium hover:bg-[#001f7a] disabled:opacity-60">
                {busy ? "Saving…" : "Save invoice"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {modal?.kind === "detail" && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div className="bg-white w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}
               data-testid="ap-detail-modal">
            <div className="sticky top-0 bg-white px-5 py-3 border-b flex justify-between items-start">
              <div>
                <div className="phizo-label text-[10px] text-[#a1a1aa]">AP · {modal.inv.status.toUpperCase()}</div>
                <h3 className="font-heading text-xl font-black tracking-tighter">
                  {modal.inv.supplier_name} · <span className="mono">{modal.inv.number}</span>
                </h3>
                <div className="text-xs text-[#52525b] mt-1">
                  {modal.inv.invoice_date} · Net K {fmt(modal.inv.amount_net)} · VAT K {fmt(modal.inv.tax_amount)}
                </div>
              </div>
              <button onClick={() => setModal(null)} className="p-1 text-[#71717a]"><X size={16} weight="bold" /></button>
            </div>
            <div className="p-5 space-y-4">
              {/* Variances */}
              <div>
                <div className="phizo-label text-[10px] mb-2">3-way match</div>
                {modal.inv.match_status === "unmatched" && (
                  <div className="text-xs text-[#71717a]">Not yet run. Click "Run match" from the list.</div>
                )}
                {modal.inv.match_status === "matched" && (
                  <div className="border-l-4 border-[#166534] bg-[#dcfce7] p-3 text-xs text-[#166534]">
                    <CheckCircle size={14} weight="fill" className="inline mr-1" />
                    Fully matched — safe to approve.
                  </div>
                )}
                {(modal.inv.variances || []).length > 0 && (
                  <ul className="space-y-2" data-testid="ap-variances">
                    {modal.inv.variances.map((v, idx) => (
                      <li key={idx} className={`border-l-4 p-3 text-xs ${
                        v.level === "high" ? "border-[#dc2626] bg-[#fee2e2] text-[#991b1b]" :
                        "border-[#d97706] bg-[#fef3c7] text-[#92400e]"
                      }`}>
                        <div className="font-bold uppercase text-[10px] tracking-wider">{v.code}</div>
                        <div>{v.detail}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {/* Linked POs */}
              {(modal.inv.pos || []).length > 0 && (
                <div>
                  <div className="phizo-label text-[10px] mb-2">Linked purchase orders</div>
                  <div className="border border-[#e4e4e7]">
                    {modal.inv.pos.map((p) => (
                      <div key={p.id} className="flex items-center gap-3 px-3 py-2 text-xs border-b border-[#f4f4f5]">
                        <span className="mono font-semibold">{p.number}</span>
                        <span className="flex-1 truncate">{p.supplier}</span>
                        <span className="text-[10px] text-[#71717a]">{p.status}</span>
                        <span className="mono">K {fmt(p.total)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="sticky bottom-0 bg-white px-5 py-3 border-t flex justify-end gap-2">
              {modal.inv.status === "captured" && (
                <button onClick={() => runMatch(modal.inv.id)}
                        className="border border-black bg-black text-white px-4 py-1.5 text-xs hover:bg-[#27272a]">
                  Run 3-way match
                </button>
              )}
              {(modal.inv.status === "matched" || modal.inv.status === "exception") && (
                <button
                  onClick={() => {
                    if (modal.inv.match_status !== "matched") {
                      const reason = window.prompt("Force-approve reason?");
                      if (!reason) return;
                      approve(modal.inv.id, true, reason);
                    } else {
                      approve(modal.inv.id);
                    }
                  }}
                  data-testid="ap-approve-btn"
                  className="bg-[#059669] text-white px-4 py-1.5 text-xs font-medium hover:bg-[#047857] flex items-center gap-1.5">
                  <PaperPlaneTilt size={12} weight="bold" />
                  {modal.inv.match_status === "matched" ? "Approve & post" : "Override & approve"}
                </button>
              )}
              {modal.inv.status === "approved" && (
                <button onClick={() => pay(modal.inv.id)} data-testid="ap-pay-btn"
                        className="bg-[#4338ca] text-white px-4 py-1.5 text-xs font-medium hover:bg-[#3730a3] flex items-center gap-1.5">
                  <Coin size={12} weight="bold" /> Pay from Bank — Operating
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
